import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg, setToken } from "@/lib/api";
import { Loader2, Eye, EyeOff, ShieldCheck } from "@/components/icons";
import { MpiresLogo } from "@/components/Brand";

const INPUT =
  "w-full h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition";
const LABEL = "block text-sm font-medium text-slate-700 mb-1.5";

const COMMON = new Set([
  "123456", "12345678", "123456789", "senha", "password",
  "mpires", "mpires123", "abc123", "qwerty",
]);

// Item 1.2 — política mínima; mensagens específicas por regra violada.
function validatePassword(pw) {
  if ((pw || "").length < 8) return "A senha precisa ter no mínimo 8 caracteres.";
  if (!/[A-Za-z]/.test(pw)) return "A senha precisa ter pelo menos 1 letra.";
  if (!/\d/.test(pw)) return "A senha precisa ter pelo menos 1 número.";
  if (COMMON.has((pw || "").toLowerCase())) return "Essa senha é muito comum. Escolha outra.";
  return "";
}

export default function TrocarSenha() {
  const { setUser, user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(false);

  const clientErr = next && validatePassword(next);
  const mismatch = confirm && confirm !== next;

  const submit = async (e) => {
    e.preventDefault();
    const v = validatePassword(next);
    if (v) return toast.error(v);
    if (next !== confirm) return toast.error("A confirmação não confere com a nova senha.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setToken(data.access_token);
      setUser(data.user);
      toast.success("Senha atualizada");
      window.location.href = "/";
    } catch (err) {
      toast.error(errMsg(err, "Não foi possível trocar a senha"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md bg-card rounded-2xl border border-black/5 shadow-[0_10px_40px_rgb(0,0,0,0.06)] p-8" data-testid="trocar-senha-page">
        <div className="mb-6"><MpiresLogo height={128} /></div>
        <div className="flex items-center gap-2 text-brand mb-2">
          <ShieldCheck className="w-5 h-5" />
          <span className="text-xs font-semibold uppercase tracking-wider">Primeiro acesso</span>
        </div>
        <h2 className="text-2xl font-semibold text-navy">Troque sua senha</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-6">
          {user?.name ? `${user.name}, ` : ""}defina uma senha nova antes de continuar. Mínimo 8 caracteres, com letra e número.
        </p>

        <form onSubmit={submit} data-testid="trocar-senha-form" className="space-y-3">
          <div>
            <label className={LABEL}>Senha atual</label>
            <input required type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className={INPUT} data-testid="trocar-senha-atual" autoComplete="current-password" />
          </div>
          <div>
            <label className={LABEL}>Nova senha</label>
            <div className="relative">
              <input required type={showNext ? "text" : "password"} value={next} onChange={(e) => setNext(e.target.value)} className={`${INPUT} pr-10`} data-testid="trocar-senha-nova" autoComplete="new-password" />
              <button type="button" onClick={() => setShowNext((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {clientErr && <p className="text-xs text-bad mt-1" data-testid="trocar-senha-erro-politica">{clientErr}</p>}
          </div>
          <div>
            <label className={LABEL}>Confirme a nova senha</label>
            <input required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={INPUT} data-testid="trocar-senha-confirmar" autoComplete="new-password" />
            {mismatch && <p className="text-xs text-bad mt-1">A confirmação não confere.</p>}
          </div>
          <button type="submit" disabled={loading || !!clientErr || !!mismatch || !current || !next} data-testid="trocar-senha-submit"
            className="w-full h-11 mt-2 rounded-lg bg-brand text-white font-semibold text-sm hover:bg-brand-hover transition-colors flex items-center justify-center disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Trocar senha"}
          </button>
        </form>
      </div>
    </div>
  );
}
